import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Link, Text } from '@reveni-io/reveni-ui'

import LinkExpiredModal from '../components/LinkExpiredModal/LinkExpiredModal'
import PortalLayout from '../components/PortalLayout/PortalLayout'
import { orderTransactionTypes } from '../constants'
import AppContext from '../context/AppContext'
import initValue from '../context/initValue'
import { getPortalBasePath } from '../helpers/customDomain/customDomain'
import { navigateTo } from '../helpers/location/location'
import { getSeonStreamStorageEntries, restoreSeonStreamStorageEntries } from '../helpers/seon/stream'
import useCheckOrder from '../hooks/useCheckOrder/useCheckOrder'
import usePortalPage from '../hooks/usePortalPage/usePortalPage'
import useTransactionTypes from '../hooks/useTransactionTypes/useTransactionTypes'
import routes from '../Router/routes.constants'
import useGiftReturn from './hooks/useGiftReturn/useGiftReturn'
import useLinkExpiredModal from './hooks/useLinkExpiredModal/useLinkExpiredModal'
import useSeonSessionMonitoring from './hooks/useSeonSessionMonitoring/useSeonSessionMonitoring'
import OrderDescription from './OrderDescription/OrderDescription'
import OrderErrorModal from './OrderErrorModal/OrderErrorModal'
import OrderForm from './OrderForm/OrderForm'
import ReturnPortalSelector from './ReturnPortalSelector/ReturnPortalSelector'
import { getAllReturnPortals, getIdentifierAndOrderNumberFromUrl, isRecoveryTokenExpired } from './helpers'
import * as Styles from './styles'

const MerchantHomePage = () => {
  const navigate = useNavigate()
  const { context, setContext } = useContext(AppContext)
  const { slug } = useParams()
  const portalBasePath = getPortalBasePath(slug)
  const routerLocation = useLocation()
  const { t } = useTranslation()
  const {
    isGiftReturn,
    handleReturnToRegularOrder,
    handleReturnToGiftReturn,
    giftRecipient,
    setGiftRecipient,
    handleGiftRecipientChange,
  } = useGiftReturn()

  const { recoveryToken: recoveryTokenFromUrl } = getIdentifierAndOrderNumberFromUrl(routerLocation.search)
  const isRecoveryLinkExpired = isRecoveryTokenExpired(recoveryTokenFromUrl)
  const { showLinkExpiredModal, closeLinkExpiredModal } = useLinkExpiredModal(
    routerLocation.state?.linkExpired || isRecoveryLinkExpired
  )

  const { checkOrder, orderLoading, type, showOrderErrorModal, closeOrderErrorModal } = useCheckOrder({
    isGiftReturn,
    giftRecipient,
  })

  const { returnPortalInfo, loadingReturnPortalInfo, changeLanguage, descriptionText } = usePortalPage({
    slug,
    isGiftReturn,
  })

  useSeonSessionMonitoring({ returnPortalId: returnPortalInfo?.id })

  const { allowsUnifiedFlow } = useTransactionTypes()

  const updateContextFromUrlParams = () => {
    const newContext = { ...initValue }
    const { identifier, orderNumber, joinGroup, recoveryToken } = getIdentifierAndOrderNumberFromUrl(
      routerLocation.search
    )

    if (identifier) newContext.orderInfo.identifier = identifier
    if (orderNumber) newContext.orderInfo.orderNumber = orderNumber
    if (joinGroup) newContext.orderInfo.joinGroup = joinGroup === 'true'
    if (recoveryToken && !isRecoveryLinkExpired) newContext.orderRecovery = { token: recoveryToken }

    setContext(newContext)

    if (newContext.orderRecovery.token) navigate(`${portalBasePath}${routes.recoveredOrdersPage}`, { replace: true })
  }

  const preserveLanguageFromLocalStorage = () => {
    const language = localStorage.language
    const seonStreamEntries = getSeonStreamStorageEntries()

    localStorage.clear()

    if (language) localStorage.language = language

    restoreSeonStreamStorageEntries(seonStreamEntries)
  }

  const updatePageInfoFromStore = () => {
    if (returnPortalInfo) {
      const faviconUrl = returnPortalInfo?.theme?.returns_favicon_url
      const name = returnPortalInfo?.name

      if (faviconUrl) {
        const favicon = document.querySelector('link[rel="icon"]')
        const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]')

        if (favicon) favicon.href = faviconUrl

        if (appleTouchIcon) appleTouchIcon.href = faviconUrl
      }

      if (name) document.title = `${name} | Returns & Exchanges`
    }
  }

  useEffect(updatePageInfoFromStore, [returnPortalInfo])
  useEffect(updateContextFromUrlParams, [
    setContext,
    navigate,
    portalBasePath,
    isRecoveryLinkExpired,
    routerLocation.search,
  ])
  useEffect(preserveLanguageFromLocalStorage, [])

  const saveOriginalShippingAddress = () => {
    setContext(context => ({
      ...context,
      orderInfo: {
        ...context.orderInfo,
        original_shipping_address: context.orderInfo.shipping_address,
      },
    }))
  }

  useEffect(saveOriginalShippingAddress, [context?.orderInfo?.shipping_address, setContext])

  const handleSubmit = (e, type = orderTransactionTypes.refund) => {
    if (type === orderTransactionTypes.refund || type === orderTransactionTypes.mixed) e.preventDefault()

    checkOrder({ orderNumber: context.orderInfo.orderNumber, identifier: context.orderInfo.identifier, type })
  }

  const handleChangeForm = (property, value) => {
    const newContext = { ...context }
    newContext.orderInfo[property] = value
    setContext(newContext)
  }

  const handleRequestNewLink = () => navigate(`${portalBasePath}${routes.findOrdersPage}`)

  const handleChangeReturnPortal = slug => {
    let baseUrl = `${window.location.protocol}//${import.meta.env.VITE_APP_DOMAIN}/${slug}`

    if (context.orderInfo.identifier && context.orderInfo.orderNumber) {
      baseUrl += `?identifier=${context.orderInfo.identifier}&orderNumber=${context.orderInfo.orderNumber}`
    }

    navigateTo(baseUrl)
  }

  const orderRecoveryPath = context?.merchantInfo?.preferences?.allows_order_recovery
    ? `${portalBasePath}${routes.findOrdersPage}`
    : null

  return (
    <PortalLayout loading={loadingReturnPortalInfo} descriptionText={descriptionText} onChangeLanguage={changeLanguage}>
      <Styles.Info>
        <OrderDescription isGiftReturn={isGiftReturn} />
        {returnPortalInfo?.preferences?.returns_policy_url && (
          <Link href={returnPortalInfo?.preferences?.returns_policy_url} target="_blank" rel="noreferrer">
            <Text type="linkS">{t('Ver política de devoluciones')}</Text>
          </Link>
        )}
      </Styles.Info>
      {context?.merchantInfo?.siblingReturnPortals?.length > 0 && (
        <ReturnPortalSelector
          returnPortals={getAllReturnPortals(context?.merchantInfo)}
          onChange={handleChangeReturnPortal}
          value={context?.merchantInfo?.slug}
        />
      )}
      <OrderForm
        identifier={context.orderInfo.identifier}
        handleSubmit={handleSubmit}
        orderNumber={context.orderInfo.orderNumber}
        orderLoading={orderLoading}
        setIdentifier={value => handleChangeForm('identifier', value)}
        setOrderNumber={value => handleChangeForm('orderNumber', value)}
        transactionTypes={returnPortalInfo?.preferences?.transaction_types}
        type={type}
        merchantCountry={returnPortalInfo?.store?.merchant?.country?.code}
        allowsUnifiedFlow={allowsUnifiedFlow}
        isGiftReturn={isGiftReturn}
        giftRecipient={giftRecipient}
        setGiftRecipient={setGiftRecipient}
        onGiftRecipientChange={handleGiftRecipientChange}
        onReturnToRegularOrder={handleReturnToRegularOrder}
        allowsGiftReturns={context?.merchantInfo?.preferences?.allows_gift_returns}
        onReturnToGiftReturn={handleReturnToGiftReturn}
        skipIdentifierValidation={context?.merchantInfo?.preferences?.skip_identifier_validation}
        orderRecoveryPath={orderRecoveryPath}
      />
      <OrderErrorModal onClose={closeOrderErrorModal} show={showOrderErrorModal} />
      <LinkExpiredModal
        show={showLinkExpiredModal}
        onRequestNewLink={handleRequestNewLink}
        onBack={closeLinkExpiredModal}
      />
    </PortalLayout>
  )
}

export default MerchantHomePage
